import React from 'react';
import { useAuthStore } from '../../store/authStore';
import styled from 'styled-components';
import { motion } from 'framer-motion';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// ✅ Современный Loading экран
const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: 
    radial-gradient(circle at 20% 20%, rgba(123, 104, 238, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(135, 206, 235, 0.1) 0%, transparent 50%),
    var(--tg-theme-bg-color, #ffffff);
  padding: 40px 20px;
`;

const LoadingCard = styled(motion.div)`
  background: linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: 40px 32px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(123, 104, 238, 0.15);
  max-width: 320px;
  width: 100%;
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid rgba(123, 104, 238, 0.1);
  border-top: 4px solid #7B68EE;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 24px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: var(--tg-theme-text-color, #000000);
  margin-bottom: 12px;
`;

const LoadingDescription = styled.p`
  font-size: 15px;
  color: var(--tg-theme-hint-color, rgba(0,0,0,0.6));
  line-height: 1.4;
  margin: 0;
`;

const ErrorContainer = styled(LoadingContainer)`
  /* Наследует стили от LoadingContainer */
`;

const ErrorCard = styled(LoadingCard)`
  background: linear-gradient(145deg, 
    rgba(239, 68, 68, 0.1) 0%, 
    rgba(220, 38, 38, 0.05) 100%
  );
  border-color: rgba(239, 68, 68, 0.2);
`;

const ErrorIcon = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto 24px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #EF4444;
  font-size: 28px;
`;

const ErrorTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #EF4444;
  margin-bottom: 12px;
`;

const ErrorDescription = styled.p`
  font-size: 15px;
  color: var(--tg-theme-hint-color, rgba(0,0,0,0.6));
  line-height: 1.4;
  margin-bottom: 24px;
`;

const RetryButton = styled(motion.button)`
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  color: white;
  border: none;
  border-radius: 16px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(123, 104, 238, 0.3);
  }
`;

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, error, checkAuth } = useAuthStore();

  // ✅ Loading состояние
  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingCard
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <LoadingSpinner />
          <LoadingTitle>Авторизация</LoadingTitle>
          <LoadingDescription>
            Подключаемся к вашему аккаунту...
          </LoadingDescription>
        </LoadingCard>
      </LoadingContainer>
    );
  }

  // ✅ Error состояние
  if (error) {
    return (
      <ErrorContainer>
        <ErrorCard
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Ошибка авторизации</ErrorTitle>
          <ErrorDescription>{error}</ErrorDescription>
          <RetryButton
            onClick={() => {
              checkAuth();
              // Можно добавить reload страницы если нужно
              // window.location.reload();
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            🔄 Повторить попытку
          </RetryButton>
        </ErrorCard>
      </ErrorContainer>
    );
  }

  // ✅ Если авторизован - показываем контент
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // ✅ Если не авторизован и нет ошибки - показываем загрузку
  // (это состояние пока идет авторизация)
  return (
    <LoadingContainer>
      <LoadingCard
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <LoadingSpinner />
        <LoadingTitle>Подключение</LoadingTitle>
        <LoadingDescription>
          Настраиваем ваш профиль в Geltek...
        </LoadingDescription>
      </LoadingCard>
    </LoadingContainer>
  );
};